import React, { useContext, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import SupportAccordion from '../../components/support-accordion/support-accordion';
import { useOwnerManualContent } from './hook/use-owner-manual-content';
import './owner-manual.scss';
import { VehicleSelectorFds } from '../../../components/sections';

import {
    notify,
    SlackMessages,
} from '../../../services/slack-message-service/SlackMessageService';
import ServiceHandler from '../../../services/service-handler';
import ServerContext from '../../../contexts/serverContext';
import SupportFdsWrapper from '../../components/support-fds-wrapper/support-fds-wrapper';
import { InfoIconToolTip } from '../../../components/common/info-icon-tooltip/info-icon-tooltip';
import siteMapService from '../../services/site-map-service/site-map-service';
import { findByAlias } from '../../../routes';
import { FdsChevron } from '../../../components/common/chevron/fds-chevron';
import { useAnalytics } from '../../../hooks/use-analytics';
import DisclosuresLinkSyndicationAware from '../../../components/sections/disclosures-link/disclosures-link-syndication-aware';
import { OWNER_MANUALS } from '../../support-constants';
import useSeoConfig from '../../../hooks/use-seo-config';
import ImportantInfoAccordion from '../../../components/sections/important-info-acccordion/important-info-accordion';
import BreadcrumbsAndViewBanner from '../../components/breadcrumbs-and-view-banner/breadcrumbs-and-view-banner';
import WebFlashMessage from '../../../components/common/web-flash-message/web-flash-message';
import { useVehicleSelectorTabsContent } from '../../../components/common/vehicle-selector-tabs/hooks/use-vehicle-selector-tabs-content';
import VehicleSelectorTabs from '../../../components/common/vehicle-selector-tabs/vehicle-selector-tabs';
import { OwnerManualLoading } from '../owner-manual-details/owner-manual-loading';
import useFooterDisplay from '../../../hooks/use-footer-display';
import {
    displayEUFooter,
    hideEUFooter,
} from '../../../components/utils/syndicate-util/syndicate-util';
import { EU_COUNTRIES } from '../../../constants';

const sendSlackNotification = (locale: string) => {
    notify(
        SlackMessages.AEM_CONTENT_MISSING + 'Owner manual in ' + locale,
        OWNER_MANUALS.SLACK_ALERT_OWNER_MANUALS_AEM_API
    );
};

const OwnerManualPage = () => {
    const serverContext = useContext(ServerContext);
    const [loading, setLoading] = useState(true);
    const vehicleSelectorTabsContent = useVehicleSelectorTabsContent();
    let ownerManualContent = useOwnerManualContent();
    let timeOut: any = null;
    const startTimeout = () => {
        timeOut = setTimeout(() => {
            if (loading) {
                setLoading(false);
                ownerManualContent = null;
            }
        }, 2000);
    };

    const baseUrl = siteMapService.getBaseUrl(
        serverContext.currentLanguageRegionCode,
        serverContext.brand
    );

    serverContext.canonical = baseUrl.domain
        .concat(baseUrl.root)
        .concat(
            siteMapService
                .getPathFromRouterConfigs(
                    serverContext.currentLanguageRegionCode,
                    serverContext.brand,
                    'OwnerManualPage',
                    []
                )
                ?.toString() || ''
        );

    const currentRoot = serverContext.root
        ? serverContext.root.substring(0, serverContext.root.length - 1)
        : '';
    const [fireEvents] = useAnalytics();
    const ownerManualShortcodeName = 'owner-manuals';
    useSeoConfig('owner-manuals-landing', 'owner-manuals');
    useEffect(() => {
        fireEvents(
            ['owner-owner-manuals'],
            undefined,
            { ownerManuals: ownerManualShortcodeName },
            false
        );
        startTimeout();
        sessionStorage.removeItem('QUERY_VIN');
        localStorage.removeItem('USER_VIN');
        return () => {
            clearTimeout(timeOut);
        };
    }, []);

    useEffect(() => {
        if (!ownerManualContent) {
            sendSlackNotification(
                ServiceHandler.AppConfigurationService.getLanguageRegionCode()
            );
        }
    }, [ownerManualContent]);

    const vinTooltip = (
        <InfoIconToolTip
            osbServiceTooltip={true}
            tooltipInfoIconClass={'dark'}
            tooltipHeader={ownerManualContent?.vinTooltipHeader}
            tooltipContent={ownerManualContent?.vinTooltipContent || ''}
            customShowTooltipAriaLabel={ownerManualContent?.vinTooltipAriaLabel}
        />
    );
    useFooterDisplay(
        serverContext.brand === 'ford'
            ? [ownerManualContent]
            : [ownerManualContent, ownerManualContent?.relatedTopicsContent]
    );

    useEffect(() => {
        if (
            serverContext.brand === 'ford' &&
            EU_COUNTRIES.includes(serverContext.currentRegionCode)
        ) {
            hideEUFooter();
        }
    }, []);

    useEffect(() => {
        if (
            serverContext.brand === 'ford' &&
            EU_COUNTRIES.includes(serverContext.currentRegionCode) &&
            ownerManualContent &&
            ownerManualContent?.relatedTopicsContent
        ) {
            displayEUFooter();
        }
    }, [ownerManualContent, ownerManualContent?.relatedTopicsContent]);

    return (
        <>
            {ownerManualContent && ownerManualContent.title !== 'loading' ? (
                <SupportFdsWrapper>
                    <WebFlashMessage
                        experience={'owner-manual'}
                        fragmentName={'experience-notification-banner'}
                    />
                    <BreadcrumbsAndViewBanner
                        currentPage={ownerManualContent.title}
                        hashId={'support'}
                    />
                    {ownerManualContent && (
                        <>
                            <div className="owner-manual-content">
                                <div className="width-90-mobile">
                                    <h1>{ownerManualContent.title}</h1>
                                    <h2>{ownerManualContent.subtitle}</h2>
                                    <p className="owner-manual-content-1">
                                        {ownerManualContent.subtitleDescription}
                                    </p>
                                </div>

                                {vehicleSelectorTabsContent &&
                                vehicleSelectorTabsContent.vehicleSelectorTabsTitle ? (
                                    <VehicleSelectorTabs
                                        redirectData={{
                                            path: siteMapService.getPathFromRouterConfigs(
                                                serverContext.currentLanguageRegionCode,
                                                serverContext.brand,
                                                'OwnerManualDetailsPage',
                                                []
                                            ),
                                            id: 'owner-manual',
                                        }}
                                        content={vehicleSelectorTabsContent}
                                    />
                                ) : (
                                    <VehicleSelectorFds
                                        tooltip={vinTooltip}
                                        redirectData={{
                                            path: findByAlias(
                                                'OwnerManualDetailsPage'
                                            ),
                                            id: 'owner-manual',
                                        }}
                                        labels={{
                                            vinHeader:
                                                ownerManualContent.vehicleSelectorVinSubtitle,
                                            vinPlaceholder:
                                                ownerManualContent.vehicleSelectorVinInput,
                                            yearDropdown:
                                                ownerManualContent.vehicleSelectorYearDropdownLabel,
                                            modelDropdown:
                                                ownerManualContent.vehicleSelectorModelDropdownLabel,
                                            submit:
                                                ownerManualContent.vehicleSelectorSubmitButtonLabel,
                                            disclaimer:
                                                ownerManualContent.vehicleSelectorDisclaimerLabel,
                                            header:
                                                ownerManualContent.vehicleSelectorVinSubtitle,
                                            dividerText:
                                                ownerManualContent.vehicleSelectorDividerText,
                                            vehicleSelectorModelSubtitle:
                                                ownerManualContent.vehicleSelectorModelSubtitle,
                                            loginSwitch:
                                                ownerManualContent.loginSwitchLabel,
                                            loggedInSwitch:
                                                ownerManualContent.loggedInSwitchLabel,
                                            vehicleSwitch:
                                                ownerManualContent.vehicleSwitchLabel,
                                        }}
                                        eventName={'owner-manuals-cta'}
                                    />
                                )}

                                <div className="owner-manual-sitemap-container">
                                    <span className="chevron-label">
                                        <a
                                            href={`${currentRoot}${siteMapService.getPathFromRouterConfigs(
                                                serverContext.currentLanguageRegionCode,
                                                serverContext.brand,
                                                'OwnerManualSitemapPage',
                                                []
                                            )}`}
                                            onClick={() => {
                                                fireEvents(
                                                    'owner-manuals-cta',
                                                    undefined,
                                                    {
                                                        ctaType:
                                                            'view owner manuals sitemap',
                                                    },
                                                    false
                                                );
                                            }}
                                        >
                                            {ownerManualContent.sitemapLinkText}
                                        </a>
                                    </span>

                                    <FdsChevron
                                        className="sitemap-chevron"
                                        direction="right"
                                        type="filled"
                                    />
                                </div>
                                <div className="width-90-mobile">
                                    <SupportAccordion
                                        contentServiceDirectory="owner-manual"
                                        contentServiceName="owner-manual-accordion"
                                        contentServiceComponentName="accordion"
                                        header={
                                            ownerManualContent.accordionTitle
                                        }
                                        eventName="owner-manuals-faq"
                                    />
                                    {ownerManualContent.relatedTopicsContent && (
                                        <>
                                            <h2 className="info-title">
                                                {
                                                    ownerManualContent.relatedTopicsTitle
                                                }
                                            </h2>
                                            <div
                                                className="owner-manual-content-3"
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(
                                                        ownerManualContent.relatedTopicsContent
                                                    ),
                                                }}
                                            />
                                        </>
                                    )}
                                </div>
                                <DisclosuresLinkSyndicationAware />
                                <ImportantInfoAccordion aemFolder="owner-manual" />
                            </div>
                        </>
                    )}
                </SupportFdsWrapper>
            ) : (
                <OwnerManualLoading
                    ownerManualContent={ownerManualContent}
                    loading={loading}
                />
            )}
        </>
    );
};

export default OwnerManualPage;
